import React, { useMemo, useCallback } from 'react';
import { cn } from '../../utils/cn';
import { useAnimationStore } from '../../store/animationStore';
import { generateEasingCurve } from '../../utils/easing';
import {
  EasingType,
  EASING_PRESETS,
  Keyframe,
  PropertyTrack,
} from '../../../core/types/animation.types';

interface EasingPresetsProps {
  selectedKeyframe: Keyframe | null;
  selectedTrack: PropertyTrack | null;
  animationId: string | null;
  disabled?: boolean;
}

// Mini SVG curve preview dimensions
const PREVIEW_SIZE = 32;
const PREVIEW_PADDING = 4;

// Category colors for visual distinction
const CATEGORY_COLORS: Record<string, string> = {
  Basic: '#71717a',
  Standard: '#3b82f6',
  Quadratic: '#8b5cf6',
  Cubic: '#a855f7',
  Sine: '#06b6d4',
  Back: '#f97316',
  Elastic: '#ec4899',
  Bounce: '#22c55e',
  Custom: '#eab308',
};

interface CurvePreviewProps {
  easing: EasingType;
  isSelected: boolean;
  isDisabled: boolean;
  categoryColor: string;
}

const CurvePreview: React.FC<CurvePreviewProps> = ({
  easing,
  isSelected,
  isDisabled,
  categoryColor,
}) => {
  const points = useMemo(() => {
    const curvePoints = generateEasingCurve(easing, undefined, 20);
    const scale = PREVIEW_SIZE - 2 * PREVIEW_PADDING;

    return curvePoints
      .map((p) => {
        const x = PREVIEW_PADDING + p.x * scale;
        // Clamp Y to visible area and invert
        const clampedY = Math.max(0, Math.min(1, p.y));
        const y = PREVIEW_SIZE - PREVIEW_PADDING - clampedY * scale;
        return `${x},${y}`;
      })
      .join(' ');
  }, [easing]);

  return (
    <svg
      width={PREVIEW_SIZE}
      height={PREVIEW_SIZE}
      viewBox={`0 0 ${PREVIEW_SIZE} ${PREVIEW_SIZE}`}
      className="flex-shrink-0"
    >
      {/* Background */}
      <rect
        x={PREVIEW_PADDING}
        y={PREVIEW_PADDING}
        width={PREVIEW_SIZE - 2 * PREVIEW_PADDING}
        height={PREVIEW_SIZE - 2 * PREVIEW_PADDING}
        fill="transparent"
        stroke={isSelected ? '#ffffff' : '#3f3f46'}
        strokeWidth={1}
        rx={2}
      />

      {/* Linear reference line */}
      <line
        x1={PREVIEW_PADDING}
        y1={PREVIEW_SIZE - PREVIEW_PADDING}
        x2={PREVIEW_SIZE - PREVIEW_PADDING}
        y2={PREVIEW_PADDING}
        stroke="#3f3f46"
        strokeWidth={1}
        strokeDasharray="2,2"
      />

      {/* Easing curve */}
      <polyline
        points={points}
        fill="none"
        stroke={isDisabled ? '#52525b' : isSelected ? '#ffffff' : categoryColor}
        strokeWidth={isSelected ? 2 : 1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Start and end dots */}
      <circle
        cx={PREVIEW_PADDING}
        cy={PREVIEW_SIZE - PREVIEW_PADDING}
        r={2}
        fill={isDisabled ? '#52525b' : isSelected ? '#ffffff' : categoryColor}
      />
      <circle
        cx={PREVIEW_SIZE - PREVIEW_PADDING}
        cy={PREVIEW_PADDING}
        r={2}
        fill={isDisabled ? '#52525b' : isSelected ? '#ffffff' : categoryColor}
      />
    </svg>
  );
};

export const EasingPresets: React.FC<EasingPresetsProps> = ({
  selectedKeyframe,
  selectedTrack,
  animationId,
  disabled = false,
}) => {
  const { setKeyframeEasing, setKeyframeBezier } = useAnimationStore();

  // Group presets by category
  const categories = useMemo(() => {
    const grouped: Record<string, typeof EASING_PRESETS> = {};
    EASING_PRESETS.forEach((preset) => {
      if (!grouped[preset.category]) {
        grouped[preset.category] = [];
      }
      grouped[preset.category].push(preset);
    });
    return grouped;
  }, []);

  const handleSelectEasing = useCallback(
    (easing: EasingType) => {
      if (disabled || !selectedKeyframe || !selectedTrack || !animationId) return;

      if (easing === 'custom') {
        // For custom, also set default bezier handles
        setKeyframeBezier(animationId, selectedTrack.id, selectedKeyframe.id, {
          in: { x: 0.6, y: 1 },
          out: { x: 0.4, y: 0 },
        });
      } else {
        setKeyframeEasing(animationId, selectedTrack.id, selectedKeyframe.id, easing);
      }
    },
    [disabled, selectedKeyframe, selectedTrack, animationId, setKeyframeEasing, setKeyframeBezier]
  );

  const isDisabled = disabled || !selectedKeyframe;

  return (
    <div
      className={cn(
        'w-12 flex flex-col bg-zinc-900 border-l border-zinc-700 overflow-y-auto',
        isDisabled && 'opacity-50'
      )}
    >
      {Object.entries(categories).map(([category, presets]) => (
        <div key={category} className="flex flex-col">
          {/* Category header */}
          <div
            className="px-1 py-1 text-[9px] font-medium text-center truncate"
            style={{ color: CATEGORY_COLORS[category] || '#71717a' }}
            title={category}
          >
            {category.slice(0, 4)}
          </div>

          {/* Presets in category */}
          <div className="flex flex-col gap-0.5 px-1 pb-1">
            {presets.map((preset) => {
              const isSelected = selectedKeyframe?.easing === preset.type;

              return (
                <button
                  key={preset.type}
                  className={cn(
                    'relative flex items-center justify-center p-0.5 rounded transition-all',
                    isSelected && 'bg-zinc-700 ring-1 ring-blue-500',
                    !isSelected && !isDisabled && 'hover:bg-zinc-800',
                    isDisabled && 'cursor-not-allowed'
                  )}
                  onClick={() => handleSelectEasing(preset.type)}
                  disabled={isDisabled}
                  title={preset.label}
                >
                  <CurvePreview
                    easing={preset.type}
                    isSelected={isSelected}
                    isDisabled={isDisabled}
                    categoryColor={CATEGORY_COLORS[category] || '#71717a'}
                  />
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default EasingPresets;
